import React, { FunctionComponent, lazy, Suspense, useContext } from 'react'
import { Route, Switch } from 'react-router-dom'
import { AuthorizedRoute } from './shared/components/authorized_route/authorized_route'
import { Right } from './generated/graphql'
import { AdsConsoleHub } from './views/ads_console_hub/ads_console_hub'
import { BulkUsersInvite } from './views/manage/components/accounts/users/bulk_users_invite'
import { BulkManage } from './views/manage/components/accounts/advertisers/bulk_manage'
import { CreateAccessRequest } from './views/manage/components/accounts/advertisers/create_access_request'
import { ManageAccessRequests } from './views/manage/components/accounts/advertisers/manage_access_requests'
import { useChromeOnboardingTreatment } from 'util/use_chrome_onboarding_treatment'
import { StageDiveConfig, StageDiveConfigContext } from 'util/app_initialization/appconfig'
import { CreateSponsoredAdsAccounts } from './views/manage/components/accounts/advertisers/create_sponsored_ads_accounts'
import { BETA_FEATURE_KEY, COPER_PROJECT_KEY, isFeatureEnabled, NON_ENDEMIC_KEY } from './shared/components/evidently_experiments/experiments'

interface ModuleWrapper {
  [key: string]: FunctionComponent
}

const App = lazy(() => import('App').then((module) => ({ default: module.App })))

const PortalHome = lazy(() => import('views/portal_home/portal_home').then((module: ModuleWrapper) => ({ default: module.PortalHome })))
const Events = lazy(() => import('views/events/events').then((module: ModuleWrapper) => ({ default: module.Events })))
const EventDetails = lazy(() => import('views/events/event_details').then((module: ModuleWrapper) => ({ default: module.EventDetails })))
const MembershipStatus = lazy(() => import('views/member_status/member_status').then((module: ModuleWrapper) => ({ default: module.MembershipStatus })))
const Resources = lazy(() => import('views/resources/resources').then((module: ModuleWrapper) => ({ default: module.Resources })))
const ResourceDetails = lazy(() => import('views/resources/resource_details').then((module: ModuleWrapper) => ({ default: module.ResourceDetails })))
const Register = lazy(() => import('views/register/register').then((module: ModuleWrapper) => ({ default: module.Register })))
const EditRegistration = lazy(() => import('views/edit_registration/edit_registration').then((module: ModuleWrapper) => ({ default: module.EditRegistration })))
const EmailVerification = lazy(() =>
  import('views/email_verification/email_verification').then((module: ModuleWrapper) => ({ default: module.EmailVerification })),
)
const Manage = lazy(() => import('views/manage/manage').then((module: ModuleWrapper) => ({ default: module.Manage })))
const ManageGroup = lazy(() => import('views/manage_group/manage_group').then((module: ModuleWrapper) => ({ default: module.ManageGroup })))
const AdvertisingApi = lazy(() =>
  import('views/manage/components/advertising_api/advertising_api').then((module: ModuleWrapper) => ({ default: module.AdvertisingApi })),
)
const Certifications = lazy(() =>
  import('views/manage/components/users/certifications/certifications').then((module: ModuleWrapper) => ({ default: module.Certifications })),
)
const BetasList = lazy(() => import('views/betas/betas_list').then((module) => ({ default: module.BetasList })))
const BetaDetail = lazy(() => import('views/betas/beta_detail').then((module) => ({ default: module.BetaDetail })))

const RegisterApi = lazy(() => import('views/api_registration/api_registration').then((module: ModuleWrapper) => ({ default: module.RegisterApi })))
const Terms = lazy(() => import('views/terms/terms').then((module: ModuleWrapper) => ({ default: module.Terms })))
const UserInvitation = lazy(() =>
  import('views/invitation_acceptance/user_invitation/user_invitation').then((module: ModuleWrapper) => ({ default: module.UserInvitation })),
)
const AdvertiserInvitation = lazy(() =>
  import('views/invitation_acceptance/advertiser_invitation/advertiser_invitation').then((module: ModuleWrapper) => ({ default: module.AdvertiserInvitation })),
)
const Error = lazy(() => import('shared/components/errors/error_view').then((module) => ({ default: module.ErrorView })))
const CaseStudyRequestForm = lazy(() =>
  import('views/case_study_request/case_study_request_form').then((module: ModuleWrapper) => ({ default: module.CaseStudyRequestForm })),
)
const PageNotFoundView = React.lazy(() => import('shared/components/errors/error_page_not_found_view').then((module) => ({ default: module.PageNotFoundView })))
const POPGrowthToolkitApp = React.lazy(() => import('POPGrowthToolkit/App'))
const AAPNDiscoveryStageDiveTenantApp = React.lazy(() => import('AAPNDiscoveryStageDiveTenant/App'))
const CoperApp = React.lazy(() => import('Coper/App'))

// These endpoints are invoked from MA user and account management page
// We are allowing /manage/accounts/* routes to be visited without the account being approved within PN
// such logic is defined in routing.ts
export const HYBRID_BULK_USER_INVITE_PATH = '/manage/accounts/users/bulk_invite'
export const HYBRID_ADVERTISERS_BULK_MANAGE = '/manage/accounts/advertisers/bulk_manage'
export const HYBRID_ADVERTISERS_CREATE_ACCESS_REQUEST = '/manage/accounts/advertisers/create_access_request'
export const HYBRID_ADVERTISERS_MANAGE_ACCESS_REQUESTS = '/manage/accounts/advertisers/manage_access_requests'
export const HYBRID_ADVERTISERS_BULK_CREATE_SPONSORED_ADS_ACCOUNTS = '/manage/accounts/advertisers/create_sponsored_ads_accounts'

export const Routes = (): JSX.Element => {
  const { isConsoleEnabled } = useChromeOnboardingTreatment()
  const config = useContext<StageDiveConfig>(StageDiveConfigContext)
  const ApiRoute = isConsoleEnabled ? AdvertisingApi : Manage
  const BulkUsersInviteRoute = BulkUsersInvite
  const BulkManageRoute = BulkManage
  const CreateAccessRequestRoute = CreateAccessRequest
  const ManageAccessRequestsRoute = ManageAccessRequests

  const isDevStage = ['dev', 'alpha', 'beta'].includes(config.stage)

  return (
    <App>
      <Suspense fallback={React.Fragment}>
        <Switch>
          <AuthorizedRoute
            exact
            path={['/growth/opportunities', '/opportunities']}
            component={POPGrowthToolkitApp}
            requiredRights={[Right.OpportunitiesView]}
          />
          <AuthorizedRoute
            exact
            path={['/growth', '/growth/*']}
            component={POPGrowthToolkitApp}
            requiredRights={[Right.GoalPlanView, Right.GoalPlanEdit, Right.OpportunitiesView]}
          />
          {isDevStage && (
            <AuthorizedRoute
              exact
              path={['/coper', '/coper/*']}
              component={CoperApp}
              requiredRights={[]}
              requiredExperiment={{
                experimentKey: COPER_PROJECT_KEY,
                experimentEvaluator: isFeatureEnabled,
              }}
            />
          )}
          <AuthorizedRoute exact path="/" component={PortalHome} requiredRights={[]} />
          <AuthorizedRoute exact path="/events" component={Events} requiredRights={[]} />
          <AuthorizedRoute exact path="/events/:id" component={EventDetails} requiredRights={[]} />
          <AuthorizedRoute exact path="/member_status" component={MembershipStatus} requiredRights={[Right.PartnerRegistrationView]} />
          <AuthorizedRoute exact path="/resources" component={Resources} requiredRights={[]} />
          <AuthorizedRoute exact path="/resources/:id" component={ResourceDetails} requiredRights={[]} />
          <AuthorizedRoute exact path="/register" component={Register} requiredRights={[]} />
          <AuthorizedRoute
            exact
            path="/edit-registration"
            component={EditRegistration}
            requiredRights={[Right.PartnerRegistrationView, Right.PartnerRegistrationEdit]}
          />
          <AuthorizedRoute exact path="/register-api" component={RegisterApi} requiredRights={[]} />
          <AuthorizedRoute exact path="/verify_email" component={EmailVerification} requiredRights={[]} />
          <AuthorizedRoute exact path="/manage" component={Manage} requiredRights={[]} />
          <AuthorizedRoute exact path={HYBRID_BULK_USER_INVITE_PATH} component={BulkUsersInviteRoute} requiredRights={[Right.UserMappingEdit]} />
          <AuthorizedRoute exact path={HYBRID_ADVERTISERS_BULK_MANAGE} component={BulkManageRoute} requiredRights={[Right.AdvertiserMappingEdit]} />
          <AuthorizedRoute
            exact
            path={HYBRID_ADVERTISERS_CREATE_ACCESS_REQUEST}
            component={CreateAccessRequestRoute}
            requiredRights={[Right.AdvertiserMappingEdit]}
          />
          <AuthorizedRoute
            exact
            path="/betas"
            component={BetasList}
            requiredRights={[]}
            requiredExperiment={{
              experimentKey: BETA_FEATURE_KEY,
              experimentEvaluator: isFeatureEnabled,
            }}
          />
          <AuthorizedRoute
            exact
            path="/betas/:id"
            component={BetaDetail}
            requiredRights={[]}
            requiredExperiment={{
              experimentKey: BETA_FEATURE_KEY,
              experimentEvaluator: isFeatureEnabled,
            }}
          />
          <AuthorizedRoute
            exact
            path={HYBRID_ADVERTISERS_MANAGE_ACCESS_REQUESTS}
            component={ManageAccessRequestsRoute}
            requiredRights={[Right.AdvertiserMappingEdit]}
          />
          <AuthorizedRoute
            exact
            path={HYBRID_ADVERTISERS_BULK_CREATE_SPONSORED_ADS_ACCOUNTS}
            component={CreateSponsoredAdsAccounts}
            requiredRights={[Right.BulkAccountCreationEdit]}
            requiredExperiment={{
              experimentKey: NON_ENDEMIC_KEY,
              experimentEvaluator: isFeatureEnabled,
            }}
          />
          <AuthorizedRoute
            exact
            path={['/leads', '/leads/*']}
            component={AAPNDiscoveryStageDiveTenantApp}
            requiredRights={[Right.ViewLead, Right.RespondLead]}
          />
          {isConsoleEnabled ? (
            <Route exact path="/manage/users/certifications" component={Certifications} />
          ) : (
            <AuthorizedRoute exact path="/manage/users/certifications" component={Manage} requiredRights={[Right.UserMappingEdit]} />
          )}
          <AuthorizedRoute exact path={'/manage/api'} component={ApiRoute} requiredRights={[Right.LwaMappingView]} />
          <AuthorizedRoute exact path={'/manage/directory_listing'} component={Manage} requiredRights={[Right.PartnerProfileEdit]} />
          <AuthorizedRoute exact path="/manage/users/linked" component={Manage} requiredRights={[Right.UserMappingEdit]} />
          <AuthorizedRoute exact path={['/manage/users', '/manage/users/invited']} component={Manage} requiredRights={[Right.UserMappingEdit]} />
          <Route
            exact
            path="/help/:id"
            component={() => {
              window.location.replace(`${config.hostname}/help`)
              return null
            }}
          />
          <AuthorizedRoute exact path={['/case_study_request_form']} component={CaseStudyRequestForm} requiredRights={[Right.PartnerProfileEdit]} />
          <Route path={['/terms/:selectedLocale', '/terms']} component={Terms} />
          <Route path="/user_invitation/:invitationId" component={UserInvitation} />
          <Route path="/advertiser_invitation/:invitationId" component={AdvertiserInvitation} />
          <Route path="/ads_hub" component={AdsConsoleHub} />
          <Route path={'/manage_group'} component={ManageGroup} />
          <Route
            path={['/switch_partners', '/manage_partners']}
            component={() => {
              window.location.replace(`${config.hostname}/am/managerAccounts/list`)
              return null
            }}
          />
          <Route
            exact
            path={'/contact'}
            component={() => {
              window.location.replace(`${config.hostname}/contactus`)
              return null
            }}
          />
          <Route path={['/error/:errorType', '/error', '/*']} component={Error} />
        </Switch>
      </Suspense>
    </App>
  )
}
